import parse from 'html-react-parser';
import { IntlShape } from 'react-intl';
import { Edge, Node } from 'reactflow';

import {
  AutomationApplicationFragment as AutomationApplicationType,
  AutomationTriggerBlockKindEnum,
  AutomationFragment as AutomationType,
  CareProfileActionableFieldEnum
} from '@typing/Generated';
import { getCareProfileActionableFieldCategory } from '@utils/careProfileUtils';
import { joinListWithCommasAndAnd } from '@utils/stringUtils';

type AutomationActionBlock = Extract<AutomationType['blocks'][number], { __typename: 'AutomationActionBlock' }>;
type AutomationConditionBlock = Extract<AutomationType['blocks'][number], { __typename: 'AutomationConditionBlock' }>;
type AutomationTimingBlock = Extract<AutomationType['blocks'][number], { __typename: 'AutomationTimingBlock' }>;
type AutomationTriggerBlock = Extract<AutomationType['blocks'][number], { __typename: 'AutomationTriggerBlock' }>;

type AutomationApplicationActor = AutomationApplicationType['activity']['actor'];

export const actorName = (actor: AutomationApplicationActor) => {
  if (actor?.__typename === 'Admin' || actor?.__typename === 'Client' || actor?.__typename === 'Expert') {
    return actor.fullName;
  } else if (actor?.__typename === 'Automation') {
    return actor.title;
  }
};

export const automationActionBlockDescription = (block: AutomationActionBlock, intl: IntlShape) => {
  const taskVisibility = block.actionTaskClientVisible
    ? intl.formatMessage({ id: 'models.automationActionBlock.taskClientVisible' })
    : intl.formatMessage({ id: 'models.automationActionBlock.taskExpertOnly' });

  const taskDueHour = block.taskDueHour && intl.formatMessage({ id: `models.hour.hourOptions.${block.taskDueHour}` });

  const taskKind = block.taskKind && intl.formatMessage({ id: `models.tasks.kind.${block.taskKind}` });

  const userType =
    block.actionBlockUserType &&
    intl
      .formatMessage({ id: `models.automationTriggerBlock.userTypeOptions.${block.actionBlockUserType}` })
      .toLowerCase();

  const message = intl.formatMessage(
    {
      id: `models.automationActionBlock.description.${block.actionBlockKind}`
    },
    {
      archiveTag: block.archiveTag,
      articleTitle: block.article?.title,
      carePlanTaskTemplate: block.carePlanTaskTemplate?.title,
      duration: block.actionDuration,
      durationUnits: block.actionDurationUnits,
      goalTemplate: block.goalTemplate?.title,
      tag: block.tag,
      taskDueDays: block.taskDueDays,
      taskDueHour,
      taskKind,
      taskTitle: block.taskTitle,
      taskVisibility,
      userType,
      worksheetTitle: block.worksheetTemplate?.title
    }
  );
  return message;
};

export const automationConditionBlockDescription = (block: AutomationConditionBlock, intl: IntlShape) => {
  if (block.automationConditionGroups.length === 0) {
    return intl.formatMessage({ id: 'models.automationConditionBlock.description.noConditionGroups' });
  }

  const groupDescriptions = block.automationConditionGroups.map(conditionGroup => {
    const conditionDescriptions = conditionGroup.automationConditions.map(condition =>
      intl.formatMessage(
        {
          id: `models.automationConditionBlock.description.conditionDescriptions.${condition.kind}`
        },
        {
          careProfileField: condition.careProfileField
            ? `<em>${intl.formatMessage({
                id: `models.${getCareProfileActionableFieldCategory(condition.careProfileField)}.questions.${
                  condition.careProfileField
                }.content`
              })}</em>`
            : '',
          careProfileValues:
            condition.careProfileField && condition.careProfileValues
              ? `<em>${joinListWithCommasAndAnd(
                  condition.careProfileValues?.map(value =>
                    intl.formatMessage({
                      id: `models.${getCareProfileActionableFieldCategory(
                        condition.careProfileField as CareProfileActionableFieldEnum
                      )}.questions.${condition.careProfileField}.options.${value}.content`
                    })
                  ),
                  intl,
                  'dictionary.or'
                )}</em>`
              : '',
          duration: condition.duration,
          durationUnits: condition.durationUnits,
          employerName: condition.employer ? condition.employer.name : intl.formatMessage({ id: 'dictionary.selfPaid' })
        }
      )
    );
    const conditionsDescription = conditionDescriptions.join(
      intl.formatMessage({
        id: `models.automationConditionBlock.description.conditionGroupOperatorJoins.${conditionGroup.operation}`
      })
    );
    return intl.formatMessage(
      { id: 'models.automationConditionBlock.description.conditionGroupDescription' },
      { conditions: conditionsDescription }
    );
  });
  const fullDescription = groupDescriptions.join(
    intl.formatMessage({ id: 'models.automationConditionBlock.description.conditionGroupJoin' })
  );

  return parse(fullDescription);
};

export const automationTimingBlockDescription = (block: AutomationTimingBlock, intl: IntlShape) => {
  const durationUnitsCopy = intl.formatMessage(
    { id: `models.automationTimingBlock.nodeUnits.${block.timingDurationUnits}` },
    { count: block.timingDuration }
  );

  return intl.formatMessage(
    { id: 'models.automationTimingBlock.nodeDescription' },
    { duration: block.timingDuration, durationUnits: durationUnitsCopy }
  );
};

export const automationTriggerBlockDescription = (block: AutomationTriggerBlock, intl: IntlShape) => {
  if (block.triggerBlockKind === AutomationTriggerBlockKindEnum.NEVER) {
    return intl.formatMessage({ id: 'models.automationTriggerBlock.description.never' });
  }

  let activityKind = '';
  if (block.activityKind) {
    activityKind = intl.formatMessage({ id: `models.activity.kindOptions.${block.activityKind}` });
  }

  let signedDocumentKind = '';
  if (block.signedDocumentKind) {
    signedDocumentKind = intl.formatMessage({
      id: `models.signedDocument.kindOptions.${block.signedDocumentKind}`
    });
  }

  let trackerType = '';
  if (block.trackerType) {
    trackerType = intl
      .formatMessage({
        id: `models.tracker.typeOptions.${block.trackerType}`
      })
      .toLowerCase();
  }

  let careProfileField = '';
  if (block.careProfileField) {
    careProfileField = intl.formatMessage({
      id: `models.${getCareProfileActionableFieldCategory(block.careProfileField)}.questions.${
        block.careProfileField
      }.content`
    });
  }

  let careProfileValues = '';
  if (block.careProfileField && block.careProfileValues) {
    const field = block.careProfileField;
    careProfileValues = joinListWithCommasAndAnd(
      block.careProfileValues?.map(
        value =>
          `"${intl.formatMessage({
            id: `models.${getCareProfileActionableFieldCategory(field)}.questions.${field}.options.${value}.content`
          })}"`
      ),
      intl,
      'dictionary.or'
    );
  }

  const taskClientVisible = intl.formatMessage({
    id: `models.automationTriggerBlock.description.taskClientVisible.${block.triggerTaskClientVisible}`
  });

  let taskKind = '';
  if (block.taskKind === null) {
    taskKind = intl.formatMessage({ id: 'models.automationTriggerBlock.description.taskKindAny' });
  } else {
    taskKind = intl.formatMessage(
      { id: 'models.automationTriggerBlock.description.taskKind' },
      { taskKind: intl.formatMessage({ id: `models.tasks.kind.${block.taskKind}` }) }
    );
  }

  let kind = intl.formatMessage(
    { id: `models.automationTriggerBlock.description.kinds.${block.triggerBlockKind}` },
    {
      activityKind,
      articleTitle: block.article?.title,
      careProfileField,
      careProfileValues,
      duration: block.triggerDuration,
      durationUnits: block.triggerDurationUnits,
      goalTemplate: block.goalTemplate?.title,
      signedDocumentKind,
      tag: block.tag,
      taskClientVisible,
      taskKind,
      trackerType
    }
  );

  if (block.triggerBlockKind === AutomationTriggerBlockKindEnum.ARTICLE_OPENED && !block.articleId) {
    kind = intl.formatMessage({ id: 'models.automationTriggerBlock.description.kinds.ARTICLE_OPENED_ANY' });
  }

  if (
    block.triggerBlockKind === AutomationTriggerBlockKindEnum.CARE_PROFILE_UPDATED &&
    block.careProfileValues &&
    block.careProfileValues.length > 0
  ) {
    kind = intl.formatMessage(
      {
        id: 'models.automationTriggerBlock.description.kinds.CARE_PROFILE_UPDATED_WITH_VALUES'
      },
      {
        base: kind,
        careProfileValues:
          block.careProfileField && block.careProfileValues
            ? `${joinListWithCommasAndAnd(
                block.careProfileValues?.map(value =>
                  intl.formatMessage({
                    id: `models.${getCareProfileActionableFieldCategory(
                      block.careProfileField as CareProfileActionableFieldEnum
                    )}.questions.${block.careProfileField}.options.${value}.content`
                  })
                ),
                intl,
                'dictionary.or'
              )}`
            : ''
      }
    );
  }

  if (block.triggerBlockKind === AutomationTriggerBlockKindEnum.GOAL_ADDED && !block.goalTemplateId) {
    kind = intl.formatMessage({ id: 'models.automationTriggerBlock.description.kinds.GOAL_ADDED_ANY' });
  }

  if (block.triggerBlockKind === AutomationTriggerBlockKindEnum.JOURNEY_TAG_ADDED && !block.tag) {
    kind = intl.formatMessage({ id: 'models.automationTriggerBlock.description.kinds.JOURNEY_TAG_ADDED_ANY' });
  }

  const userType = intl.formatMessage({
    id: `models.automationTriggerBlock.description.userTypes.${block.triggerBlockUserType}`
  });

  const frequency = intl.formatMessage({
    id: `models.automationTriggerBlock.description.frequencies.${block.frequency}`
  });

  if (block.triggerBlockKind === AutomationTriggerBlockKindEnum.TIME_SINCE_LAST_CONSULT) {
    return intl.formatMessage(
      { id: 'models.automationTriggerBlock.description.formatWithoutUser' },
      { frequency, kind }
    );
  }

  return intl.formatMessage({ id: 'models.automationTriggerBlock.description.format' }, { frequency, kind, userType });
};

type Position = {
  x: number;
  y: number;
};

const positionNode = (
  node: Node,
  nodes: Node[],
  edges: Edge[],
  offset: Position,
  isFalseKid: boolean,
  childXPositions: number[]
) => {
  node.position = offset;

  if (node.type === 'AutomationTerminatorBlockNode') {
    if (!isFalseKid) {
      node.position.x += 150;
    }
  } else if (node.type === 'AutomationConditionBlockNode') {
    const trueEdge = edges.find(edge => edge.sourceHandle === 'CONDITION_TRUE' && edge.source === node.id) as Edge;
    const falseEdge = edges.find(edge => edge.sourceHandle === 'CONDITION_FALSE' && edge.source === node.id) as Edge;
    const trueKid = nodes.find(node => node.id === trueEdge.target) as Node;
    const falseKid = nodes.find(node => node.id === falseEdge.target) as Node;
    const trueBranchXPositions: number[] = [];
    positionNode(
      trueKid,
      nodes,
      edges,
      {
        x: offset.x,
        y: offset.y + (node.height as number) + 50
      },
      false,
      trueBranchXPositions
    );

    positionNode(
      falseKid,
      nodes,
      edges,
      {
        x: Math.max(...trueBranchXPositions) + 50,
        y: offset.y + (node.height as number) + 50
      },
      true,
      childXPositions
    );
  } else {
    const kidEdge = edges.find(edge => edge.source === node.id) as Edge;
    const kidNode = nodes.find(node => node.id === kidEdge.target) as Node;

    positionNode(
      kidNode,
      nodes,
      edges,
      { x: offset.x, y: offset.y + (node.height as number) + 50 },
      false,
      childXPositions
    );
  }

  childXPositions.push(node.position.x + (node.width as number));
};

export const layoutAutomation = (nodes: Node[], edges: Edge[]) => {
  let needsRerender = false;

  nodes.forEach(node => {
    const domNode = document.querySelector(`.react-flow__node[data-id="${node.id}"]`);
    if (node.type === 'AutomationTerminatorBlockNode') {
      node.width = 100;
    } else {
      node.width = 400;
    }
    node.height = domNode?.clientHeight ?? 75;

    if (!domNode) {
      needsRerender = true;
    }
  });

  const rootNode = nodes.find(node => node.type === 'AutomationTriggerBlockNode');

  if (rootNode) {
    positionNode(rootNode, nodes, edges, { x: 25, y: 25 }, false, []);
  }

  return needsRerender;
};
